// 演练回放组件：把 OperationRecord 列表按时间轴动态回放，并对照标准处方
// - 顶部固定播放控制条（进度条 / 播放暂停 / 重置 / 倍速）
// - 左栏"学员操作"：已执行高亮（正确绿/错误红），未来灰色半透明，最近一条 ring 高亮
// - 右栏"标准处方"：学员操作命中某步骤后标记 ✓ 已完成
// - 底部对比摘要：总数 / 正确 / 错误 / 处方完成率

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Play,
  Pause,
  RotateCcw,
  CheckCircle2,
  XCircle,
  Activity,
  Target,
  Clock,
  Gauge,
} from 'lucide-react';
import type { OperationRecord, DrillStep } from '@/types';

export interface DrillReplayProps {
  /** 学员操作序列（无需预排序，组件内部按 timestamp 排序） */
  operations: OperationRecord[];
  /** 标准处方（fault.steps.filter(s => s.correct)） */
  correctSteps?: DrillStep[];
  /** 演练开始时间（毫秒时间戳） */
  startTime: number;
  /** 演练结束时间（毫秒时间戳） */
  endTime: number;
  /** 故障名（标题用） */
  faultName?: string;
}

const SPEEDS: ReadonlyArray<number> = [1, 2, 4];

/** 文本归一化：去空格、转小写，便于松散匹配 */
function normalize(s: string | undefined | null): string {
  return (s ?? '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');
}

/**
 * 学员操作 action 是否命中某条标准步骤
 * 规则：归一化后完全相等，或存在包含关系（任一方向）
 */
function actionMatches(studentAction: string, stepAction: string): boolean {
  const a = normalize(studentAction);
  const b = normalize(stepAction);
  if (!a || !b) return false;
  if (a === b) return true;
  if (a.includes(b) || b.includes(a)) return true;
  return false;
}

/** 毫秒 → 可读时长（如 "3.2s" / "1m05s"） */
function fmtMs(ms: number): string {
  const sec = Math.max(0, ms / 1000);
  if (sec < 60) return `${sec.toFixed(1)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}m${String(s).padStart(2, '0')}s`;
}

export const DrillReplay: React.FC<DrillReplayProps> = ({
  operations,
  correctSteps,
  startTime,
  endTime,
  faultName,
}) => {
  // —— 数据预处理 ——
  const sortedOps = useMemo(
    () => [...operations].sort((a, b) => a.timestamp - b.timestamp),
    [operations],
  );
  const steps = useMemo(
    () => (correctSteps ?? []).filter((s) => s.correct).sort((a, b) => a.order - b.order),
    [correctSteps],
  );

  // 防止 startTime === endTime 造成除零
  const duration = Math.max(1, endTime - startTime);

  // —— 回放状态 ——
  const [currentReplayTime, setCurrentReplayTime] = useState<number>(startTime);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [speed, setSpeed] = useState<number>(1);

  const rafRef = useRef<number | null>(null);
  const lastTickRef = useRef<number | null>(null);

  // requestAnimationFrame 驱动的时间推进
  useEffect(() => {
    if (!isPlaying) {
      lastTickRef.current = null;
      return;
    }
    const tick = (now: number) => {
      if (lastTickRef.current == null) lastTickRef.current = now;
      const delta = now - lastTickRef.current;
      lastTickRef.current = now;
      setCurrentReplayTime((prev) => {
        const next = prev + delta * speed;
        if (next >= endTime) {
          setIsPlaying(false);
          return endTime;
        }
        return next;
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      lastTickRef.current = null;
    };
  }, [isPlaying, speed, endTime]);

  // 组件卸载时确保取消动画帧
  useEffect(() => {
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const handleTogglePlay = useCallback(() => {
    // 已到末尾再按播放：先重置到起点
    if (currentReplayTime >= endTime) {
      setCurrentReplayTime(startTime);
      setIsPlaying(true);
      return;
    }
    setIsPlaying((p) => !p);
  }, [currentReplayTime, endTime, startTime]);

  const handleReset = useCallback(() => {
    setIsPlaying(false);
    setCurrentReplayTime(startTime);
  }, [startTime]);

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value);
    setIsPlaying(false);
    setCurrentReplayTime(startTime + (val / 100) * duration);
  };

  // —— 派生展示数据 ——
  const progress = Math.min(
    100,
    Math.max(0, ((currentReplayTime - startTime) / duration) * 100),
  );

  const executedOps = useMemo(
    () => sortedOps.filter((o) => o.timestamp <= currentReplayTime),
    [sortedOps, currentReplayTime],
  );
  const futureOps = useMemo(
    () => sortedOps.filter((o) => o.timestamp > currentReplayTime),
    [sortedOps, currentReplayTime],
  );
  // 最近一条已执行操作（用于动画强调）
  const currentOp = executedOps.length > 0 ? executedOps[executedOps.length - 1] : null;
  const currentOpTs = currentOp?.timestamp ?? null;

  // 命中的标准步骤 id 集合
  const matchedStepIds = useMemo(() => {
    const matched = new Set<string>();
    for (const op of executedOps) {
      for (const step of steps) {
        if (!matched.has(step.id) && actionMatches(op.action, step.action)) {
          matched.add(step.id);
        }
      }
    }
    return matched;
  }, [executedOps, steps]);

  // 统计
  const totalOps = sortedOps.length;
  const executedCount = executedOps.length;
  const correctCount = executedOps.filter((o) => o.isCorrect).length;
  const wrongCount = executedOps.filter((o) => !o.isCorrect).length;
  const correctStepTotal = steps.length;
  const matchedCount = matchedStepIds.size;
  const completionRate =
    correctStepTotal > 0 ? Math.round((matchedCount / correctStepTotal) * 100) : 0;

  // 当前操作距上一条的时间间隔
  const currentGapMs = useMemo(() => {
    if (!currentOp) return null;
    const idx = sortedOps.findIndex((o) => o === currentOp);
    if (idx <= 0) return Math.max(0, currentOp.timestamp - startTime);
    return Math.max(0, currentOp.timestamp - sortedOps[idx - 1].timestamp);
  }, [currentOp, sortedOps, startTime]);

  const elapsedMs = Math.max(0, currentReplayTime - startTime);
  const totalMs = duration;
  const isAtEnd = currentReplayTime >= endTime;

  return (
    <div className="bg-bg-secondary border border-border rounded-lg overflow-hidden flex flex-col">
      {/* ============ 顶部：标题 + 播放控制条（固定） ============ */}
      <div className="border-b border-border bg-bg-tertiary/40 px-4 py-3 space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Activity className="w-4 h-4 text-primary" />
            演练回放
            {faultName && <span className="text-text-muted font-normal">· {faultName}</span>}
          </div>
          <div className="text-[11px] text-text-muted font-mono tabular-nums">
            {fmtMs(elapsedMs)} / {fmtMs(totalMs)}
          </div>
        </div>

        {/* 进度条 */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleTogglePlay}
            title={isPlaying ? '暂停' : '播放'}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-md bg-primary hover:bg-primary-dark text-white transition-colors"
          >
            {isPlaying ? (
              <Pause className="w-4 h-4" />
            ) : (
              <Play className="w-4 h-4 translate-x-[1px]" />
            )}
          </button>
          <button
            type="button"
            onClick={handleReset}
            title="重置到起点"
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-md bg-bg-tertiary hover:bg-border text-text-secondary transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
          <input
            type="range"
            min={0}
            max={100}
            step={0.1}
            value={progress}
            onChange={handleSeek}
            className="flex-1 min-w-0"
            aria-label="回放进度"
          />
          <div className="flex items-center gap-0.5 flex-shrink-0 bg-bg-tertiary rounded-md p-0.5">
            {SPEEDS.map((sp) => (
              <button
                key={sp}
                type="button"
                onClick={() => setSpeed(sp)}
                className={`px-2 py-0.5 text-[11px] font-mono rounded transition-colors ${
                  speed === sp
                    ? 'bg-primary text-white'
                    : 'text-text-muted hover:text-text-primary'
                }`}
              >
                {sp}x
              </button>
            ))}
          </div>
        </div>
        {isAtEnd && (
          <div className="text-[11px] text-cyan-400 flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" />
            回放已结束
          </div>
        )}
      </div>

      {/* ============ 中部：左右双栏（可滚动） ============ */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border max-h-[420px] overflow-y-auto">
        {/* 左栏：学员操作 */}
        <div className="bg-bg-secondary p-3 space-y-1.5">
          <div className="flex items-center justify-between sticky top-0 bg-bg-secondary pb-1.5 -mx-3 px-3 z-10 border-b border-border/60">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-text-primary">
              <Activity className="w-3.5 h-3.5 text-cyan-400" />
              学员操作
            </div>
            <span className="text-[10px] text-text-muted font-mono">
              {executedCount}/{totalOps}
            </span>
          </div>

          {sortedOps.length === 0 ? (
            <p className="text-xs text-text-muted text-center py-6">无操作记录</p>
          ) : (
            <div className="space-y-1.5 pt-1.5">
              {/* 已执行操作 */}
              {executedOps.map((op, i) => {
                const isCurrent = op.timestamp === currentOpTs;
                const prevTs = i > 0 ? executedOps[i - 1].timestamp : startTime;
                const gap = Math.max(0, op.timestamp - prevTs);
                return (
                  <div
                    key={`exec-${i}`}
                    className={`flex items-start gap-2 text-xs px-2 py-1.5 rounded border transition-all ${
                      op.isCorrect
                        ? 'border-green-500/50 bg-green-500/5'
                        : 'border-red-500/50 bg-red-500/5'
                    } ${isCurrent ? 'ring-2 ring-primary animate-pulse' : ''}`}
                  >
                    <span className="text-text-muted shrink-0 font-mono">#{i + 1}</span>
                    {op.isCorrect ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-400 mt-0.5 shrink-0" />
                    ) : (
                      <XCircle className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="truncate text-text-primary">{op.action ?? '未知操作'}</div>
                      {op.aiFeedback && (
                        <div className="text-[10px] text-text-muted mt-0.5 truncate">
                          {op.aiFeedback}
                        </div>
                      )}
                      {op.parameterChange && (
                        <div className="text-[10px] text-cyan-400/80 mt-0.5 font-mono">
                          {op.parameterChange.param}: {op.parameterChange.from} →{' '}
                          {op.parameterChange.to}
                        </div>
                      )}
                      {isCurrent && currentGapMs !== null && (
                        <div className="text-[10px] text-primary mt-0.5 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          距上一步 +{fmtMs(currentGapMs)}
                        </div>
                      )}
                    </div>
                    <span className="text-[10px] text-text-muted shrink-0 font-mono">
                      +{fmtMs(gap)}
                    </span>
                  </div>
                );
              })}

              {/* 未来操作（灰色半透明） */}
              {futureOps.map((op, i) => (
                <div
                  key={`future-${i}`}
                  className="flex items-start gap-2 text-xs px-2 py-1.5 rounded border border-border/50 bg-bg-tertiary/30 opacity-40"
                >
                  <span className="text-text-muted shrink-0 font-mono">
                    #{executedOps.length + i + 1}
                  </span>
                  <span className="text-text-muted">·</span>
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-text-muted">{op.action ?? '未知操作'}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 右栏：标准处方 */}
        <div className="bg-bg-secondary p-3 space-y-1.5">
          <div className="flex items-center justify-between sticky top-0 bg-bg-secondary pb-1.5 -mx-3 px-3 z-10 border-b border-border/60">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-text-primary">
              <Target className="w-3.5 h-3.5 text-purple-400" />
              标准处方
            </div>
            <span className="text-[10px] text-text-muted font-mono">
              {matchedCount}/{correctStepTotal}
            </span>
          </div>

          {steps.length === 0 ? (
            <p className="text-xs text-text-muted text-center py-6">
              未提供标准处方
              <br />
              <span className="text-[10px]">（该故障未匹配到 correct 步骤）</span>
            </p>
          ) : (
            <div className="space-y-1.5 pt-1.5">
              {steps.map((step) => {
                const matched = matchedStepIds.has(step.id);
                return (
                  <div
                    key={step.id}
                    className={`flex items-start gap-2 text-xs px-2 py-1.5 rounded border transition-all ${
                      matched
                        ? 'border-green-500/50 bg-green-500/10'
                        : 'border-border/50 bg-bg-tertiary/30'
                    }`}
                  >
                    <span className="text-text-muted shrink-0 font-mono">{step.order}</span>
                    {matched ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-400 mt-0.5 shrink-0" />
                    ) : (
                      <span className="w-3.5 h-3.5 mt-0.5 shrink-0 rounded-full border border-border-light" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div
                        className={`truncate ${matched ? 'text-green-300' : 'text-text-secondary'}`}
                      >
                        {step.action}
                      </div>
                      {matched && (
                        <div className="text-[10px] text-green-400 mt-0.5">✓ 已完成</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ============ 底部：对比摘要 ============ */}
      <div className="border-t border-border bg-bg-tertiary/40 px-4 py-2.5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <SummaryCell label="总操作数" value={`${executedCount}/${totalOps}`} accent="text-text-primary" />
          <SummaryCell
            label="正确"
            value={String(correctCount)}
            accent="text-green-400"
            icon={<CheckCircle2 className="w-3.5 h-3.5" />}
          />
          <SummaryCell
            label="错误"
            value={String(wrongCount)}
            accent="text-red-400"
            icon={<XCircle className="w-3.5 h-3.5" />}
          />
          <SummaryCell
            label="处方完成率"
            value={`${completionRate}%`}
            accent="text-purple-400"
            icon={<Gauge className="w-3.5 h-3.5" />}
          />
        </div>
      </div>
    </div>
  );
};

const SummaryCell: React.FC<{
  label: string;
  value: string;
  accent: string;
  icon?: React.ReactNode;
}> = ({ label, value, accent, icon }) => (
  <div className="flex items-center gap-1.5">
    {icon && <span className={accent}>{icon}</span>}
    <div>
      <div className="text-[10px] text-text-muted">{label}</div>
      <div className={`font-mono font-medium ${accent}`}>{value}</div>
    </div>
  </div>
);

export default DrillReplay;
